import { useIsMutating } from "@tanstack/react-query";
import cn from "classnames";
import { useDebouncedValue } from "hooks";
import { StaffManagerPermissions, useProfile } from "modules/common";
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Tooltip } from "react-tooltip";
import { Loader, Modal, Pagination } from "ui";

import { AiLesson } from "../../aiAgents.types";
import {
  lessonReplayMutationKey,
  useGetAssistants,
  useGetLessons,
  useLessonMutations,
  useSetLessonsEnabled
} from "../../apis";
import {
  AI_LESSONS_PAGE_SIZE,
  AI_LESSON_LIMITS,
  AI_LESSON_NON_DISCRIMINATING_TAGS
} from "../../constants/lessons";
import {
  LESSON_TABS,
  LessonRowControls,
  LessonTab,
  activeLessonReplayBadgeKey,
  isEndpointMissingError,
  lessonRowControls,
  lessonTabCount,
  lessonTabStatuses,
  lessonTotalCount
} from "../../helpers/lessons";
import { replayState } from "../../helpers/replay";
import { LessonEditModal } from "../LessonEditModal";
import { LessonFirePreviewModal } from "../LessonFirePreviewModal";
import { LessonReplayModal } from "../LessonReplayModal";

import styles from "./LessonsTab.module.scss";

interface Props {
  assistantId: string;
}

// One react-tooltip instance for the whole tab; anchors carry their text in
// data-tooltip-content.
const TOOLTIP_ID = "ai-lessons-tab-tooltip";

const SEARCH_DEBOUNCE_MS = 400;

const TAB_LABEL_KEY: Record<LessonTab, string> = {
  active: "aiLesson.tab.active",
  drafts: "aiLesson.tab.drafts",
  paused: "aiLesson.tab.paused",
  archived: "aiLesson.tab.archived"
};

const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 18 18" fill="none">
    <path
      d="M16 16l-3.5-3.5M14 8a6 6 0 1 1-12 0 6 6 0 0 1 12 0Z"
      stroke="#7E7E8B"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const formatDate = (iso: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString(undefined, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
};

const ToggleSwitch = ({
  checked,
  disabled,
  label,
  tooltip,
  small,
  onClick
}: {
  checked: boolean;
  disabled?: boolean;
  label: string;
  tooltip?: string;
  small?: boolean;
  onClick: () => void;
}) => (
  // The tooltip sits on a wrapper: a disabled button gets no hover events.
  <span
    className={styles.switchWrap}
    data-tooltip-id={tooltip ? TOOLTIP_ID : undefined}
    data-tooltip-content={tooltip}
  >
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      className={cn(styles.switch, {
        [styles.switchOn]: checked,
        [styles.switchSmall]: small
      })}
      disabled={disabled}
      onClick={onClick}
    >
      <span className={styles.switchKnob} />
    </button>
  </span>
);

const LessonRow = ({
  lesson,
  controls,
  busy,
  onToggle,
  onActivate,
  onEdit,
  onReplay,
  onPreview,
  onArchive,
  onRestore
}: {
  lesson: AiLesson;
  controls: LessonRowControls;
  busy: boolean;
  onToggle: (isEnabled: boolean) => void;
  onActivate: () => void;
  onEdit: () => void;
  onReplay: () => void;
  onPreview: () => void;
  onArchive: () => void;
  onRestore: () => void;
}) => {
  const { t } = useTranslation();
  const { toggle, activate, replay } = controls;
  // Activating (or resuming) needs a replay judged "Fixed", unless the admin
  // is Super (lessonCanActivate).
  const replayRequired = t("aiLesson.replayRequired");
  const replayBadgeKey = activeLessonReplayBadgeKey(lesson);
  // This browser is waiting for R1 on this lesson (the list is only
  // refreshed when it answers), or the server's marker says a replay runs.
  const replayPending =
    useIsMutating({
      mutationKey: lessonReplayMutationKey(lesson.assistantId),
      predicate: (mutation) =>
        (mutation.state.variables as { lessonId?: string } | undefined)
          ?.lessonId === lesson.id
    }) > 0;
  const replayRunning =
    replayPending ||
    replayState(lesson.lastReplayResult, Date.now()) === "running";

  return (
    <li className={cn(styles.row, { [styles.rowBusy]: busy })}>
      <div className={styles.rowHeader}>
        <div className={styles.rowTitle}>{lesson.title}</div>
        {toggle ? (
          <ToggleSwitch
            small
            checked={toggle.checked}
            disabled={busy || !toggle.enabled}
            label={t("aiLesson.switch")}
            tooltip={toggle.enabled ? undefined : replayRequired}
            onClick={() => onToggle(!toggle.checked)}
          />
        ) : null}
      </div>

      <div className={styles.badges}>
        <span
          className={cn(styles.badge, styles[`status_${lesson.status}`])}
          title={t("aiLesson.column.status")}
        >
          {t(`aiLesson.status.${lesson.status}`)}
        </span>
        <span
          className={cn(styles.badge, styles.badgeCategory)}
          title={t("aiLesson.column.type")}
        >
          {t(`aiLesson.category.${lesson.category}`)}
        </span>
        {replayBadgeKey ? (
          <span className={cn(styles.badge, styles.badgeWarning)}>
            {t(replayBadgeKey)}
          </span>
        ) : null}
        {replayRunning ? (
          <span className={cn(styles.badge, styles.badgeRunning)}>
            {t("aiLesson.replayRunning")}
          </span>
        ) : null}
      </div>

      <p className={styles.guidance}>{lesson.guidance}</p>

      <div className={styles.chipsRow}>
        <span className={styles.chipsLabel}>
          {t("aiLesson.column.situations")}
        </span>
        {lesson.triggers.tags.map((tag) => (
          <span
            key={tag}
            className={cn(styles.chip, {
              [styles.chipSecondary]:
                AI_LESSON_NON_DISCRIMINATING_TAGS.includes(tag)
            })}
          >
            {t(`aiLesson.tag.${tag}`)}
          </span>
        ))}
        {lesson.triggers.keywords.map((keyword) => (
          <span key={keyword} className={cn(styles.chip, styles.chipKeyword)}>
            « {keyword} »
          </span>
        ))}
        {lesson.triggers.messageTypes.map((type) => (
          <span key={type} className={cn(styles.chip, styles.chipType)}>
            {t(`aiLesson.messageType.${type}`)}
          </span>
        ))}
      </div>

      <dl className={styles.facts}>
        <div className={styles.fact}>
          <dt>{t("aiLesson.column.uses")}</dt>
          <dd>{lesson.usageCount.toLocaleString()}</dd>
        </div>
        <div className={styles.fact}>
          <dt>{t("aiLesson.column.lastUsed")}</dt>
          <dd>
            {lesson.lastUsedAt
              ? formatDate(lesson.lastUsedAt)
              : t("aiLesson.neverUsed")}
          </dd>
        </div>
        <div className={styles.fact}>
          <dt>{t("aiLesson.column.replay")}</dt>
          <dd>
            {t(`aiLesson.replay.${lesson.replayStatus}`)}
            {lesson.lastReplayAt ? (
              <span className={styles.factSub}>
                {formatDate(lesson.lastReplayAt)}
              </span>
            ) : null}
          </dd>
        </div>
      </dl>

      <div className={styles.actions}>
        {controls.edit ? (
          <button
            type="button"
            className={styles.action}
            disabled={busy}
            onClick={onEdit}
          >
            {t("aiLesson.action.edit")}
          </button>
        ) : null}
        {activate ? (
          <span
            data-tooltip-id={activate.enabled ? undefined : TOOLTIP_ID}
            data-tooltip-content={activate.enabled ? undefined : replayRequired}
          >
            <button
              type="button"
              className={styles.action}
              disabled={busy || !activate.enabled}
              onClick={onActivate}
            >
              {t(activate.label)}
            </button>
          </span>
        ) : null}
        {replay ? (
          <span
            data-tooltip-id={replay.tooltip ? TOOLTIP_ID : undefined}
            data-tooltip-content={
              replay.tooltip ? t(replay.tooltip) : undefined
            }
          >
            <button
              type="button"
              className={styles.action}
              disabled={busy || !replay.enabled}
              onClick={onReplay}
            >
              {t("aiLesson.action.replay")}
            </button>
          </span>
        ) : controls.viewReplay ? (
          <button
            type="button"
            className={styles.action}
            disabled={busy}
            onClick={onReplay}
          >
            {t("aiLesson.action.viewReplay")}
          </button>
        ) : null}
        {controls.preview ? (
          <button
            type="button"
            className={styles.action}
            disabled={busy}
            onClick={onPreview}
          >
            {t("aiLesson.action.preview")}
          </button>
        ) : null}
        {controls.archive ? (
          <button
            type="button"
            className={cn(styles.action, styles.actionMuted)}
            disabled={busy}
            onClick={onArchive}
          >
            {t("aiLesson.action.archive")}
          </button>
        ) : null}
        {controls.restore ? (
          <button
            type="button"
            className={styles.action}
            disabled={busy}
            onClick={onRestore}
          >
            {t("aiLesson.action.restore")}
          </button>
        ) : null}
      </div>
    </li>
  );
};

// Lessons of the WhatsApp assistant: the assistant-level switch, the four
// sub-tabs, and per lesson its switch, usage, replay status and actions
// (the replay opens LessonReplayModal).
// Every mutation is hidden without the "Manage AI lessons" permission (the
// backend checks it again). Until the backend serving lessons is deployed,
// its routes answer 404 and the tab shows a quiet notice instead.
const LessonsTab = ({ assistantId }: Props) => {
  const { t } = useTranslation();
  const { user } = useProfile({ enabled: false });
  const isSuper = user.role === "Super";
  const canManage = Boolean(
    isSuper ||
    user.permissions?.includes(StaffManagerPermissions.ManageAiLessons)
  );

  const { assistants } = useGetAssistants();
  const assistant = assistants.find((a) => a.id === assistantId);

  const [tab, setTab] = useState<LessonTab>("active");
  const [search, setSearch] = useState("");
  const trimmedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS).trim();
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<AiLesson | null>(null);
  const [previewing, setPreviewing] = useState<AiLesson | null>(null);
  const [replaying, setReplaying] = useState<AiLesson | null>(null);
  const [confirmEnable, setConfirmEnable] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  // The replay route answered 404 (a backend without it): the action is
  // hidden for the rest of the visit, the rest of the tab still works.
  const [replayUnavailable, setReplayUnavailable] = useState(false);

  const statuses = useMemo(() => lessonTabStatuses(tab), [tab]);
  const {
    lessons,
    meta,
    counts,
    isLoading,
    isError,
    isPreviousData,
    isUnavailable: isListUnavailable
  } = useGetLessons(assistantId, {
    statuses,
    search: trimmedSearch,
    page,
    take: AI_LESSONS_PAGE_SIZE
  });

  const { toggleLesson, activateLesson, archiveLesson, restoreLesson } =
    useLessonMutations(assistantId);
  const setLessonsEnabled = useSetLessonsEnabled(assistantId);

  useEffect(() => {
    setPage(1);
  }, [tab, trimmedSearch]);

  // The last lesson of the last page moved to another tab: go back a page.
  useEffect(() => {
    if (meta && meta.pageCount > 0 && page > meta.pageCount) {
      setPage(meta.pageCount);
    }
  }, [meta, page]);

  const rowMutations = [
    toggleLesson,
    activateLesson,
    archiveLesson,
    restoreLesson
  ];
  const busyLessonId =
    rowMutations.find((mutation) => mutation.isLoading)?.variables?.lessonId ??
    null;

  // The hooks already toast real errors; a missing route hides the tab.
  const onMutationError = (error: unknown) => {
    if (isEndpointMissingError(error)) setUnavailable(true);
  };

  const handleToggle = (lesson: AiLesson, isEnabled: boolean) =>
    toggleLesson.mutate(
      { lessonId: lesson.id, isEnabled },
      {
        onSuccess: () =>
          toast.success(
            t(isEnabled ? "aiLesson.toast.resumed" : "aiLesson.toast.paused")
          ),
        onError: onMutationError
      }
    );

  const handleActivate = (lesson: AiLesson) =>
    activateLesson.mutate(
      { lessonId: lesson.id },
      {
        onSuccess: () => toast.success(t("aiLesson.toast.activated")),
        onError: onMutationError
      }
    );

  const handleArchive = (lesson: AiLesson) =>
    archiveLesson.mutate(
      { lessonId: lesson.id },
      {
        onSuccess: () => toast.success(t("aiLesson.toast.archived")),
        onError: onMutationError
      }
    );

  const handleRestore = (lesson: AiLesson) =>
    restoreLesson.mutate(
      { lessonId: lesson.id },
      {
        onSuccess: () => toast.success(t("aiLesson.toast.restored")),
        onError: onMutationError
      }
    );

  // While the request runs, the switch already shows the asked value.
  const lessonsEnabled = setLessonsEnabled.isLoading
    ? Boolean(setLessonsEnabled.variables?.lessonsEnabled)
    : Boolean(assistant?.lessonsEnabled);

  const applyLessonsEnabled = (next: boolean) =>
    setLessonsEnabled.mutate(
      { lessonsEnabled: next },
      {
        onSuccess: () => {
          setConfirmEnable(false);
          toast.success(
            t(
              next
                ? "aiLesson.killSwitch.enabledToast"
                : "aiLesson.killSwitch.disabledToast"
            )
          );
        },
        onError: (error) => {
          setConfirmEnable(false);
          onMutationError(error);
        }
      }
    );

  // Turning lessons off is immediate (it is the emergency stop); turning
  // them on asks first, because it changes what real customers receive.
  const handleKillSwitch = () => {
    if (lessonsEnabled) {
      applyLessonsEnabled(false);
    } else {
      setConfirmEnable(true);
    }
  };

  if (unavailable || isListUnavailable) {
    return <div className={styles.notice}>{t("aiLesson.unavailable")}</div>;
  }

  const rowControls = (lesson: AiLesson): LessonRowControls => {
    const controls = lessonRowControls(lesson, { isSuper, canManage });
    return replayUnavailable ? { ...controls, replay: null } : controls;
  };

  const activeCount = counts?.active ?? 0;
  const totalCount = lessonTotalCount(counts);
  const hasNoLessons = Boolean(counts) && totalCount === 0;

  return (
    <div className={styles.wrapper}>
      {!canManage ? (
        <div className={styles.notice}>{t("aiLesson.noPermission")}</div>
      ) : null}

      <section className={styles.killSwitch}>
        <div className={styles.killSwitchRow}>
          <div className={styles.killSwitchText}>
            <div className={styles.killSwitchTitle}>
              {t("aiLesson.killSwitch.title")}
            </div>
            <div className={styles.killSwitchSub}>
              {lessonsEnabled
                ? t("aiLesson.killSwitch.on")
                : t("aiLesson.killSwitch.off")}
            </div>
          </div>
          <ToggleSwitch
            checked={lessonsEnabled}
            disabled={!canManage || !assistant || setLessonsEnabled.isLoading}
            label={t("aiLesson.killSwitch.title")}
            tooltip={canManage ? undefined : t("aiLesson.noPermission")}
            onClick={handleKillSwitch}
          />
        </div>
        <div className={styles.warning}>{t("aiLesson.killSwitch.warning")}</div>
        {!lessonsEnabled && activeCount > 0 ? (
          <div className={styles.killSwitchSub}>
            {t("aiLesson.killSwitch.offWithActive", { count: activeCount })}
          </div>
        ) : null}
      </section>

      {isLoading && !counts ? (
        <div className={styles.loaderWrap}>
          <Loader />
        </div>
      ) : isError && !counts ? (
        <div className={styles.notice}>{t("aiLesson.loadFailed")}</div>
      ) : hasNoLessons ? (
        <div className={styles.empty}>{t("aiLesson.empty")}</div>
      ) : (
        <>
          <div className={styles.tabs}>
            {LESSON_TABS.map((key) => (
              <button
                key={key}
                type="button"
                className={cn(styles.tab, { [styles.tabActive]: tab === key })}
                aria-pressed={tab === key}
                onClick={() => setTab(key)}
              >
                {t(TAB_LABEL_KEY[key])}
                <span className={styles.tabCount}>
                  {lessonTabCount(counts, key).toLocaleString()}
                </span>
              </button>
            ))}
          </div>

          <div className={styles.searchBox}>
            <span className={styles.searchIcon}>
              <SearchIcon />
            </span>
            <input
              type="search"
              className={styles.searchInput}
              value={search}
              maxLength={AI_LESSON_LIMITS.SEARCH.MAX_LENGTH}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("aiLesson.searchPlaceholder")}
            />
          </div>

          {lessons.length > 0 ? (
            // Dimmed while another sub-tab, search or page is loading.
            <ul
              className={cn(styles.list, {
                [styles.listStale]: isPreviousData
              })}
            >
              {lessons.map((lesson) => (
                <LessonRow
                  key={lesson.id}
                  lesson={lesson}
                  controls={rowControls(lesson)}
                  busy={busyLessonId === lesson.id}
                  onToggle={(isEnabled) => handleToggle(lesson, isEnabled)}
                  onActivate={() => handleActivate(lesson)}
                  onEdit={() => setEditing(lesson)}
                  onReplay={() => setReplaying(lesson)}
                  onPreview={() => setPreviewing(lesson)}
                  onArchive={() => handleArchive(lesson)}
                  onRestore={() => handleRestore(lesson)}
                />
              ))}
            </ul>
          ) : isLoading ? (
            <div className={styles.loaderWrap}>
              <Loader />
            </div>
          ) : (
            <div className={styles.empty}>
              {trimmedSearch
                ? t("aiLesson.searchEmpty")
                : t("aiLesson.tabEmpty")}
            </div>
          )}

          <Pagination
            currentPage={page}
            onChange={setPage}
            totalItems={meta?.itemCount ?? 0}
            perPage={AI_LESSONS_PAGE_SIZE}
          />
        </>
      )}

      <LessonEditModal
        assistantId={assistantId}
        lesson={editing}
        onClose={() => setEditing(null)}
        onUnavailable={() => setUnavailable(true)}
      />

      <LessonFirePreviewModal
        assistantId={assistantId}
        lesson={previewing}
        onClose={() => setPreviewing(null)}
      />

      <LessonReplayModal
        assistantId={assistantId}
        lesson={replaying}
        canManage={canManage}
        onClose={() => setReplaying(null)}
        onUnavailable={() => setReplayUnavailable(true)}
      />

      <Modal
        isOpen={confirmEnable}
        title="aiLesson.killSwitch.confirmTitle"
        okText="aiLesson.killSwitch.confirm"
        cancelText="Cancel"
        isLoading={setLessonsEnabled.isLoading}
        onOk={() => applyLessonsEnabled(true)}
        onClose={() => setConfirmEnable(false)}
      >
        <div className={styles.confirmBody}>
          <p>{t("aiLesson.killSwitch.confirmBody")}</p>
          <p className={styles.confirmCount}>
            {t("aiLesson.killSwitch.activeCount", { count: activeCount })}
          </p>
        </div>
      </Modal>

      <Tooltip id={TOOLTIP_ID} place="top" opacity={1} offset={6} />
    </div>
  );
};

export default LessonsTab;
